import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Pages.css';
import { handleLinkClick } from '../utils/deviceUtils';
import logoStrip from '../assets/logo/logo-strip.png';
import images from '../assets/index';

const heroSlides = [
    {
        image: images.home1,
        line1: "INTELLIGENCE THAT",
        line2: "MOVES BUSINESS FORWARD",
        subtitle: "A global team committed to customer success—driven by innovation, collaboration, and a 360° approach to data transformation."
    },
    {
        image: images.newcase3,
        line1: "INNOVATE SMARTER,",
        line2: "GROW FASTER",
        subtitle: "Turning bold ideas into scalable digital solutions that drive real growth."
    },
    {
        image: images.contact1,
        line1: "INNOVATION WITH PURPOSE,",
        line2: "INTELLIGENCE WITH PRECISION",
        subtitle: "From advanced AI to next-gen cybersecurity, we engineer technology solutions that power smart, secure, and scalable enterprises."
    },
    {
        image: images.home2,
        line1: "THINK DEEPER,",
        line2: "BUILD SMARTER",
        subtitle: "Crafting intelligent systems that evolve with your business and future goals."
    },
    {
        image: images.image1,
        line1: "DRIVEN BY DATA,",
        line2: "BUILT FOR TOMORROW",
        subtitle: "Leveraging insights to create resilient, future-ready digital ecosystems."
    },
    {
        image: images.home4,
        line1: "BUILD ON DATA,",
        line2: "LEAD THE FUTURE",
        subtitle: "Empowering businesses with end-to-end big data, analytics, and protection solutions to unlock ideas and accelerate impact."
    },
];

const expertiseAreas = [
    {
        title: "Enterprise Data & AI Services",
        image: images.comp,
        description: "Empower Decisions. Govern Data. Scale Intelligence.",
        link: "/services/enterprise-data-ai-services",
        isContact: false
    },
    {
        title: "Technology & Cloud Services",
        image: images.globe,
        description: "Cloud-Native, Future-Proof, and Ready to Scale.",
        link: "/services/technology-cloud-services",
        isContact: false
    },
    {
        title: "Advanced Analytics & AI Enablement",
        image: images.robot,
        description: "From Dashboards to Decisions—Powering Real-Time Intelligence.",
        link: "/services/advanced-analytics-ai-enablement",
        isContact: false
    },
    {
        title: "Cloud Acceleration Studio",
        image: images.cloud2,
        description: "Your Fast Track to Agile, Scalable, and AI-Optimized Cloud.",
        link: "/services/cloud-acceleration-studio",
        isContact: false
    },
    {
        title: "Cybersecurity and Digital Trust",
        image: images.cyber2,
        description: "Secure by Design. Trusted by Default. Resilient by Architecture.",
        link: "/services/cybersecurity-and-digital-trust",
        isContact: false
    },
    {
        title: "Custom Solution Development",
        image: images.customsol,
        description: "Custom Solution Development Tailored To Your Vision. Built for Performance. Scaled for Tomorrow.",
        link: "/contact",
        isContact: true
    }
];

function Home() {
    const [currentSlide, setCurrentSlide] = useState(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        heroSlides.forEach(slide => {
            const img = new Image();
            img.src = slide.image;
        });

        const startInterval = () => {
            intervalRef.current = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
            }, 6000);
        };
        startInterval();
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const nextSlide = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        intervalRef.current = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 6000);
    };

    const prevSlide = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
        intervalRef.current = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 6000);
    };

    useEffect(() => {
        document.title = "Datamatics Consulting";
    }, []);
    return (
        <>
            {/* Hero Carousel */}
            <section className="hero-carousel">
                <div className="hero-slide-wrapper">
                    <img
                        key={currentSlide}
                        src={heroSlides[currentSlide].image}
                        alt={heroSlides[currentSlide].title}
                        className="hero-background-image"
                    />
                    <div className="hero-overlay">
                        <div className="hero-content">
                            <h1 className="hero-title">
                                {heroSlides[currentSlide].line1}
                                <br />
                                <h1 className="hero-title">{heroSlides[currentSlide].line2}</h1>
                            </h1>
                            <p className="hero-subtitle">
                                {heroSlides[currentSlide].subtitle}
                            </p>
                        </div>
                    </div>
                    <button className="carousel-arrow left" onClick={prevSlide}>←</button>
                    <button className="carousel-arrow right" onClick={nextSlide}>→</button>
                </div>
            </section>

            {/* Company Description */}
            <section className="company-description">
                <div className="description-content">
                    <p className="description-text">
                        Datamatics Consulting is a Pakistan-born, globally experienced Data & AI partner. We bring world-class expertise in data platforms, machine learning, and ISO-aligned operations to the enterprises shaping Pakistan's future — delivering outcomes that are measurable, scalable, and built to last.
                    </p>
                    <p className="description-text">
                        With a relentless focus on impact, we turn complexity into opportunity and
                        ideas into reality.
                    </p>
                    <div className="purple-line-main"></div>
                </div>
            </section>

            {/* Areas of Expertise */}
            <section className="expertise-section">
                <div className="">
                    <h1 className="section-title-main">Our Core Areas</h1>
                    <div className="expertise-grid">
                        {expertiseAreas.map((area, index) => (
                            <div key={index} className="expertise-card">
                                <img src={area.image} alt={area.title} className="card-image" />

                                <div className="card-content">
                                    <h3 className="card-title">{area.title}</h3>
                                </div>

                                <div className="card-hover-overlay">
                                    <p className="card-description">{area.description}</p>

                                    {area.isContact ? (
                                        <Link
                                            to="/contact"
                                            className="card-btn contact-btn"
                                            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
                                        >
                                            Contact Us
                                        </Link>
                                    ) : (
                                        <a
                                            href={area.link}
                                            className="card-btn"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleLinkClick(area.link);
                                            }}
                                        >
                                            Learn More
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Investing in a brighter future */}
            <section className="investing-section">
                <img src={images.robot3} alt="Team Meeting" className="investing-bg" />
                <div className="investing-overlay">
                    <div className="investing-content">
                        <div className="investing-headline-wrap">
                            <h2 className="investing-title">Accelerate What's Possible</h2>
                        </div>
                        <div className="investing-text">
                            <p className="para1">
                                At Datamatics Consulting, we accelerate enterprise potential through strategic investments in AI, healthcare, and immersive technologies. With a global presence and a future-first mindset, we help businesses transform faster, grow stronger, and lead their industries with purpose.</p>
                        </div>
                    </div>
                </div>
            </section>
            <div className="boundaries-text">
                Our Global Footprint
            </div>
            {/* Map Section */}
            <section className="map-section">
                <div className="map-container">
                    <img src={images.map3} alt="World Map" className="map-image" />
                    <div className="map-overlay">
                        <p className="map-tagline">A trusted technology partner, accessible to businesses everywhere</p>
                        <Link to="/contact" className="map-connect-btn-map">
                            Let's Connect
                            <span className="connect-icon">
                                <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2 6h8M6 2l4 4-4 4" stroke="#1a8fe3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </Link>
                    </div>
                </div>
            </section>
            <section className="clients-section">
                <h4 className="clients-title">Customers We Have Worked With</h4>
                <div className="ticker-wrapper">
                    <div className="ticker-track">
                        <img src={logoStrip} alt="Our Clients" className="ticker-strip" />
                        <img src={logoStrip} alt="Our Clients" className="ticker-strip" />
                    </div>
                </div>
            </section>
        </>
    );
}

export default Home;